import React, { useState, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { collections, weightSetsCollection } from "../mock";
import { useCart, formatPrice } from "../context/CartContext";
import Header from "../components/Header";
import CartDrawer from "../components/CartDrawer";
import Footer from "../components/Footer";
import TrustBadges from "../components/TrustBadges";
import StorePresence from "../components/StorePresence";
import Reviews from "../components/Reviews";
import Stars from "../components/Stars";
import { ShoppingCart, ChevronRight, SlidersHorizontal } from "lucide-react";

function Card({ p }) {
  const { addItem } = useCart();
  const d = Math.round((1 - p.price / p.compareAt) * 100);
  return (
    <div className="group flex flex-col overflow-hidden rounded-2xl border border-navy/10 bg-white shadow-sm transition-all hover:-translate-y-1 hover:shadow-xl">
      <Link to={`/product/${p.slug}`} className="relative aspect-square overflow-hidden bg-gray-50 p-4">
        {d > 0 && (
          <span className="absolute left-3 top-3 z-10 rounded-full bg-brand px-2.5 py-1 text-[11px] font-bold text-white">-{d}%</span>
        )}
        <img src={p.image} alt={p.name} className="h-full w-full object-contain transition-transform duration-500 group-hover:scale-105" />
      </Link>
      <div className="flex flex-1 flex-col p-4">
        <Stars value={p.rating} size={13} />
        <Link to={`/product/${p.slug}`} className="mt-2 line-clamp-2 min-h-[40px] text-sm font-semibold leading-snug text-navy hover:text-brand">
          {p.name}
        </Link>
        <div className="mt-2 flex items-baseline gap-2">
          <span className="font-heading text-lg font-extrabold text-navy">{formatPrice(p.price)}</span>
          {p.compareAt && <span className="text-sm text-navy/40 line-through">{formatPrice(p.compareAt)}</span>}
        </div>
        <button
          onClick={() => addItem({ id: p.id, name: p.name, price: p.price, image: p.image })}
          className="mt-3 flex items-center justify-center gap-2 rounded-full bg-navy py-2.5 text-xs font-bold uppercase tracking-wide text-white transition-colors hover:bg-brand"
        >
          <ShoppingCart size={15} /> Add to Cart
        </button>
      </div>
    </div>
  );
}

export default function CategoryPage() {
  const { slug } = useParams();
  const c = collections[slug] || weightSetsCollection;
  const [sort, setSort] = useState("featured");
  const products = useMemo(() => {
    const arr = [...c.products];
    if (sort === "low") arr.sort((a, b) => a.price - b.price);
    if (sort === "high") arr.sort((a, b) => b.price - a.price);
    if (sort === "rating") arr.sort((a, b) => b.rating - a.rating);
    return arr;
  }, [sort, c.products]);

  return (
    <div className="min-h-screen bg-white font-body">
      <Header />
      <CartDrawer />

      {/* Breadcrumb */}
      <div className="border-b border-navy/10 bg-gray-50">
        <div className="mx-auto flex max-w-7xl items-center gap-1.5 px-4 py-3 text-xs text-navy/50 lg:px-8">
          {c.breadcrumb.map((b, i) => (
            <span key={b} className="flex items-center gap-1.5">
              {i === 0 ? <Link to="/" className="hover:text-brand">{b}</Link> : <span className={i === c.breadcrumb.length - 1 ? "text-navy/80" : ""}>{b}</span>}
              {i < c.breadcrumb.length - 1 && <ChevronRight size={12} />}
            </span>
          ))}
        </div>
      </div>

      {/* Hero band */}
      <div className="bg-navy">
        <div className="mx-auto max-w-7xl px-4 py-12 lg:px-8">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand">{c.eyebrow || "Shop"}</p>
          <h1 className="mt-2 font-heading text-3xl font-extrabold text-white md:text-4xl">Browse {c.title}</h1>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-white/70">{c.description}</p>
        </div>
      </div>

      <TrustBadges />

      {/* Toolbar */}
      <div className="mx-auto max-w-7xl px-4 pt-8 lg:px-8">
        <div className="flex items-center justify-between gap-4">
          <p className="flex items-center gap-2 text-sm font-semibold text-navy">
            <SlidersHorizontal size={16} className="text-brand" /> {products.length} products
          </p>
          <div className="flex items-center gap-2">
            <label className="text-xs font-semibold text-navy/50">Sort by</label>
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="rounded-full border border-navy/20 bg-white px-4 py-2 text-sm font-medium text-navy outline-none focus:border-brand"
            >
              <option value="featured">Featured</option>
              <option value="low">Price: Low to High</option>
              <option value="high">Price: High to Low</option>
              <option value="rating">Top Rated</option>
            </select>
          </div>
        </div>
      </div>

      {/* Grid */}
      <div className="mx-auto max-w-7xl px-4 py-8 lg:px-8">
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 md:gap-5 lg:grid-cols-4">
          {products.map((p) => (
            <Card key={p.id} p={p} />
          ))}
        </div>
      </div>

      {/* Buying guide / SEO copy */}
      <section className="bg-gray-50 py-14">
        <div className="mx-auto max-w-4xl px-4 lg:px-8">
          <h2 className="font-heading text-2xl font-extrabold text-navy">Why buy your {c.title.toLowerCase()} from Cyberfit?</h2>
          <p className="mt-4 text-sm leading-relaxed text-navy/70">
            Every product in our range is chosen or engineered to a standard our mechanical-engineer founder
            would happily own himself — no short cuts to shave costs. That's why home lifters and busy
            commercial gyms across Australia keep coming back to Cyberfit.
          </p>
          <p className="mt-3 text-sm leading-relaxed text-navy/70">
            Everything is backed by our 30-day money-back guarantee, 2 year warranty and free, expert advice —
            just call Steve on 02 9620 6635 and we'll help you choose the right kit for your space and budget.
          </p>
        </div>
      </section>

      <StorePresence />
      <Reviews title="Loved by Australian lifters" subtitle="See why thousands choose Cyberfit for their home and commercial gyms." />
      <Footer />
    </div>
  );
}
